<template>
  <DataTable
    :is-loading="isLoading"
    :columns="tableColumns"
    :data="fees"
    :total-count="totalCount"
    :page-number="pageNumber"
    @update:page="onUpdatePage"
    @update:page-size="onUpdatePageSize"
  />
</template>

<script setup lang="ts">
import { NetworkError } from '@/errors';
import { getDetailedInvoiceFeesById } from '@/services/invoices';
import { useGeneralStore } from '@/stores/general';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { onMounted, ref } from 'vue';
import type { ModelInvoiceDetailedFeeResponse } from '@layup/api-sdk';
import DataTable from '../../DataTable.vue';
import { tableColumns } from './FeeDetailsColumns';

const props = defineProps<{ id: string }>();

const generalStore = useGeneralStore();

const isLoading = ref(false);
const fees = ref<Partial<ModelInvoiceDetailedFeeResponse>[]>([]);
const totalCount = ref(0);
const pageNumber = ref(1);

const skip = ref(0);
const limit = ref(generalStore.pageSize);

onMounted(async () => fetchDetailedFees());

const fetchDetailedFees = async () => {
  isLoading.value = true;

  try {
    const response = await getDetailedInvoiceFeesById(props.id, skip.value, limit.value);

    totalCount.value = response.totalCount || 0;
    fees.value = response.fees || [];
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('invoice-details-feeDetails', {
        status: 'network_error',
        channel: 'merchant',
        method: 'fetchDetailedFees',
      });
    } else {
      createException(error);
      addMetric('invoice-details-feeDetails', { status: 'error', channel: 'merchant', method: 'fetchDetailedFees' });
    }
  } finally {
    isLoading.value = false;
  }
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
  skip.value = (pageNumber.value - 1) * generalStore.pageSize;
  fetchDetailedFees();
};

const onUpdatePageSize = (size: number) => {
  pageNumber.value = 1;
  skip.value = (pageNumber.value - 1) * size;
  generalStore.pageSize = size;
  limit.value = size;
  fetchDetailedFees();
};
</script>
